/**
 * Navigation visual themes from Figma (ICM Pre-Login A 2026):
 * - `blue`  → "Blue BG" frames (navy shell, white text, peach active)
 * - `white` → "White BG" frames (white shell, ink text, orange active)
 */
export type NavTheme = "blue" | "white";

export const DEFAULT_NAV_THEME: NavTheme = "blue";

export type NavThemeStyles = {
    shell: string;
    /** `sm+` overrides when pairing a different mobile theme with this desktop theme. */
    shellFromSm: string;
    link: string;
    linkActive: string;
    search: string;
    mega: string;
    megaItem: string;
    megaSectionTitle: string;
    viewport: string;
    mobileShell: string;
    mobileItem: string;
    secondaryCta: string;
    /** Markets mega-menu outer shell */
    marketsShell: string;
    marketsSidebarTitle: string;
    marketsCategory: string;
    marketsCategoryActive: string;
    /** Markets right-hand content panel (Indices / Popular Stocks) */
    marketsPanel: string;
    marketsPanelGlow: string;
};

export const navThemeStyles: Record<NavTheme, NavThemeStyles> = {
    blue: {
        shell: "bg-nav-bg text-nav-text",
        shellFromSm: "sm:bg-nav-bg sm:text-nav-text",
        link: "text-nav-text",
        // Figma: colors/shades/sunrise-orange/200
        linkActive: "font-bold text-[#FBCBAD]",
        search: "text-nav-text",
        mega: "bg-nav-bg text-nav-text",
        megaItem:
            "text-nav-text hover:bg-nav-fg hover:font-bold focus:bg-nav-fg focus:font-bold",
        megaSectionTitle: "text-nav-text",
        viewport: "bg-nav-bg text-nav-text",
        mobileShell: "bg-nav-bg text-nav-text",
        mobileItem: "text-nav-text hover:bg-nav-fg",
        // Solid white CTA on navy (no outline needed)
        secondaryCta:
            "bg-cta-secondary text-cta-secondary-text hover:bg-cta-secondary-hover hover:text-cta-secondary-text-hover",
        marketsShell: "bg-nav-bg",
        marketsSidebarTitle: "text-nav-text",
        marketsCategory: "text-nav-text hover:bg-nav-fg",
        marketsCategoryActive: "bg-nav-fg font-bold",
        marketsPanel: "bg-white",
        marketsPanelGlow: "bg-brand-primary-200/50",
    },
    white: {
        shell: "bg-white text-ink-header",
        shellFromSm: "sm:bg-white sm:text-ink-header",
        link: "text-ink-header",
        // Figma: colors/brand-colors/sunrise-orange
        linkActive: "font-bold text-brandAccent",
        search: "text-cta-primary",
        mega: "bg-white text-ink-body",
        megaItem:
            "text-ink-body hover:bg-cta-secondary-hover hover:font-bold focus:bg-cta-secondary-hover focus:font-bold",
        megaSectionTitle: "text-ink-header",
        viewport: "bg-white text-ink-body",
        mobileShell: "bg-white text-ink-header",
        mobileItem: "text-ink-header hover:bg-cta-secondary-hover",
        // Outlined secondary on white bar
        secondaryCta:
            "border border-cta-secondary-stroke bg-cta-secondary text-cta-secondary-text hover:bg-cta-secondary-hover hover:text-cta-secondary-text-hover",
        // Figma White BG / Markets (8803:86304)
        marketsShell: "bg-white",
        marketsSidebarTitle: "text-ink-header",
        marketsCategory: "text-ink-body hover:bg-badge-neutral-100",
        marketsCategoryActive: "bg-badge-neutral-100 font-bold text-ink-body",
        // Figma: colors/shades/midnight-blue/50
        marketsPanel: "bg-[#f0f5fe]",
        marketsPanelGlow: "bg-secondary-200/40",
    },
};
